import type React from 'react';
import { useState, useMemo } from 'react';
import {
  Box,
  Flex,
  Grid,
  Link,
  Icon,
  IconButton,
  Tooltip,
  Spinner,
  Avatar,
} from '@chakra-ui/react';
import { KText } from '@/ui-library/components/text';
import { KTag } from '@/ui-library/components';
import { ViewMorePopover } from '@/components/ats/ViewMorePopover';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import { LinkExternal01 } from '@kula-ai/icons/dual-tone/LinkExternal01';
import { Edit05 } from '@kula-ai/icons/outline/Edit05';
import { PlusCircle } from '@kula-ai/icons/dual-tone/PlusCircle';
import { DetailSection } from './DetailSection';
import { UserInfoSection } from './UserInfoSection';
import { CustomFieldsSection } from './CustomFieldsSection';
import { AssociatedCandidateSection } from './AssociatedCandidateSection';
import { AssociateJobPopover } from '../AssociateJobPopover';
import { GroupRequisitionCodes } from '@/components/requisitions/PartOfGroupTag';
import dayjs from 'dayjs';
import {
  EMPLOYMENT_TYPE_CONFIG,
  REQUISITION_TYPE_CONFIG,
} from '../../constants/enums';
import { type CustomField } from '../../types';
import { useUpdateRequisitionRecruiter } from '../../hooks/useUpdateRequisitionRecruiter';
import { useAssociateJobToRequisition } from '../../hooks/useAssociateJobToRequisition';
import { useCreateJobForRequisition } from '../../hooks/useCreateJobForRequisition';
import { IconButton as KIconButton, ModalWithForm } from '@/components/ats';
import { getEditReasonFormConfig } from '../../config';
import { useSelector } from 'react-redux';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { currencyFormatterHelpers } from '@/utils/currencyFormatter';
import { useAtsPermissions } from '@/hooks/useAtsPermissions';
import { ApprovalFlowSteps } from '@/components/ats/ApprovalFlowSteps/ApprovalFlowSteps';
import { useGetApprovalsByEntityQuery } from '@/components/contacts/candidate-job-drawer/tabs/current-job/details/OfferV1/api';
import { CLOSE_REASONS } from '../../constants/closeReasons';

const { formatCurrencyNumber } = currencyFormatterHelpers;

interface RequisitionDetailsBodyProps {
  requisition: any;
  customFieldsData: CustomField[] | undefined;
  dateFormat: string;
  readOnlyMode: boolean;
  formFields: any;
  isReqAssociated: boolean;
  canEdit: boolean;
  isPartOfGroup?: boolean;
  groupRequisitions?: any[];
  currentRequisitionId?: number;
}

export const RequisitionDetailsBody: React.FC<RequisitionDetailsBodyProps> = ({
  requisition,
  customFieldsData,
  dateFormat,
  readOnlyMode = false,
  formFields,
  isReqAssociated,
  canEdit,
  isPartOfGroup = false,
  groupRequisitions = [],
  currentRequisitionId,
}) => {
  const { mutate: updateRecruiter, isPending: isUpdatingRecruiter } =
    useUpdateRequisitionRecruiter();
  const { mutate: associateJob, isPending: isAssociatingJob } =
    useAssociateJobToRequisition();
  const { jobPermissions } = useAtsPermissions();

  const { closed_by, rejection_reason, status } = requisition || {};
  const { first_name, last_name, avatar_url } = closed_by || {};
  const closedByName = `${first_name} ${last_name}`;
  // Hook for creating a new job and associating it with this requisition
  const { modalActions: createJobModalActions, modal: createJobModal } =
    useCreateJobForRequisition({
      requisitionId: requisition.id.toString(),
      onSuccess: () => {},
      requisition,
    });

  // State for reason modal
  const [isReasonModalOpen, setIsReasonModalOpen] = useState(false);
  const [pendingRecruiterId, setPendingRecruiterId] = useState<number | null>(
    null
  );
  const [pendingRequisitionId, setPendingRequisitionId] = useState<
    number | undefined
  >(undefined);
  const [pendingApplyToGroup, setPendingApplyToGroup] = useState<
    boolean | undefined
  >(undefined);

  // Fetch approval data
  const { data: approvalData, isLoading: isApprovalLoading } =
    useGetApprovalsByEntityQuery({
      entityId: requisition?.id?.toString() || '',
      entityType: 'requisition',
    });

  const salaryCurrency = requisition.salary_currency || {
    currency_code: 'USD',
    currency_symbol: '$',
  };

  const currUser = useSelector(selectCurrUser);
  const isReqApprovalEnabled = currUser?.requisition?.approval_enabled;

  // Get all form fields for checking visibility and approval requirements
  const formFieldsMap = useMemo(() => {
    if (!formFields?.ats_form_template) return {};

    const allFields = formFields.ats_form_template.static_fields.flatMap(
      (group) => group.fields
    );

    return allFields.reduce((acc, field) => {
      acc[field.variable] = field;
      return acc;
    }, {} as Record<string, any>);
  }, [formFields]);

  const recruiterNeedsReapproval =
    formFieldsMap['recruiter']?.preapproval_enabled || false;

  // Helper to check if a field is visible (not hidden)
  const isFieldVisible = (fieldVariable: string) => {
    const field = formFieldsMap[fieldVariable];
    return field && !field.hidden;
  };

  // Handler for recruiter assignment/reassignment
  const handleRecruiterAssign = (
    userId: number,
    requisitionId?: number,
    applyToGroup?: boolean
  ) => {
    const targetRequisitionId = requisitionId || requisition.id;
    // Check if approval is enabled AND field needs reapproval
    if (isReqApprovalEnabled && recruiterNeedsReapproval) {
      // Show reason modal
      setPendingRecruiterId(userId);
      setPendingRequisitionId(targetRequisitionId);
      setPendingApplyToGroup(applyToGroup);
      setIsReasonModalOpen(true);
    } else {
      // Direct update
      updateRecruiter({
        requisitionId: targetRequisitionId.toString(),
        recruiterId: userId,
        applyToGroup,
      });
    }
  };

  // Handler for reason modal submission
  const handleReasonSubmit = (values: { edit_reason: string }) => {
    if (pendingRecruiterId) {
      updateRecruiter({
        requisitionId: (pendingRequisitionId || requisition.id).toString(),
        recruiterId: pendingRecruiterId,
        needsReapproval: true,
        reapprovalNote: values.edit_reason,
        applyToGroup: pendingApplyToGroup,
      });
      setIsReasonModalOpen(false);
      setPendingRecruiterId(null);
      setPendingRequisitionId(undefined);
      setPendingApplyToGroup(undefined);
    }
  };

  const formatDate = (date?: string | null) => {
    if (!date) return '-';
    return dayjs(date).format(dateFormat);
  };

  const formatSalary = () => {
    if (!requisition?.minimum_salary && !requisition?.maximum_salary)
      return '-';

    // Construct currency string for formatter (e.g., "(USD)")
    const currencyString = `(${salaryCurrency.currency_code})`;

    const min = requisition?.minimum_salary
      ? `${salaryCurrency.currency_symbol}${formatCurrencyNumber(
          requisition.minimum_salary,
          currencyString
        )}`
      : '';
    const max = requisition?.maximum_salary
      ? `${salaryCurrency.currency_symbol}${formatCurrencyNumber(
          requisition.maximum_salary,
          currencyString
        )}`
      : '';

    if (min && max) return `${min} - ${max} (${salaryCurrency.currency_code})`;
    return min || max;
  };

  const renderApprovalFlow = () => {
    if (isApprovalLoading) {
      return (
        <Flex
          h="100%"
          w="100%"
          justifyContent="center"
          alignItems="center"
          p="24px"
        >
          <Spinner color="primary-600" />
        </Flex>
      );
    }

    return (
      <ApprovalFlowSteps
        config={approvalData || {}}
        isLoading={isApprovalLoading}
        onClose={() => {}}
        entityType="requisition"
        entityId={requisition?.id?.toString()}
        offerId={undefined}
        onSuccess={() => {}}
        renderModalInParent={false}
        viewMode={true}
        handleModalOpen={undefined}
        handleUserSelect={undefined}
        containerStyles={{
          pt: '0px !important',
        }}
      />
    );
  };

  return (
    <Box p={{ base: 4, md: 6 }}>
      <Flex direction="column" gap={6}>
        {requisition.reapproval_note && (
          <DetailSection label="Note to approvers">
            <KText whiteSpace="pre-wrap">{requisition.reapproval_note}</KText>
          </DetailSection>
        )}
        {status === 'closed' && (
          <Grid
            templateColumns={{
              base: '1fr',
              md: 'repeat(2, minmax(0, max-content))',
            }}
            gap={{ base: '16px', md: '24px' }}
          >
            <DetailSection label="Closed reason">
              <KText fontWeight="medium">
                {CLOSE_REASONS.find((r) => r.value === rejection_reason)
                  ?.label || rejection_reason}
              </KText>
            </DetailSection>
            <DetailSection label="Closed by">
              <Flex gap="6px" alignItems="center">
                <Avatar
                  size="xs"
                  width="18px"
                  height="18px"
                  className="avatar-xs"
                  src={avatar_url || undefined}
                  name={closedByName}
                  borderRadius="3px"
                  flexShrink={0}
                />
                <KText size="sm-regular" color="secondary-900">
                  {closedByName}
                </KText>
              </Flex>
            </DetailSection>
          </Grid>
        )}

        {/* Requisition ID, Role Name and Headcount */}
        <Grid
          templateColumns={{
            base: '1fr',
            md: 'repeat(2, minmax(0, max-content))',
          }}
          gap={{ base: '16px', md: '24px' }}
        >
          {!requisition.group_size && (
            <DetailSection label="Requisition ID">
              <KText>{requisition.code}</KText>
            </DetailSection>
          )}
          <DetailSection label="Role name">
            <KText fontWeight="medium">{requisition.role_name}</KText>
          </DetailSection>
          {requisition.group_size && (
            <DetailSection label="Headcount">
              <KText fontWeight="medium">{requisition.group_size}</KText>
            </DetailSection>
          )}
        </Grid>

        {/* Requisition IDs for group requisitions */}
        {requisition.group_size && groupRequisitions.length > 0 && (
          <DetailSection label="Requisition ID(s)">
            <GroupRequisitionCodes requisitions={groupRequisitions} />
          </DetailSection>
        )}

        {/* Description */}
        {isFieldVisible('description') && (
          <DetailSection label="Description">
            <KText whiteSpace="pre-wrap">
              {requisition.description || '-'}
            </KText>
          </DetailSection>
        )}

        {/* Details Grid: Department, Office, Work Type, Requisition Type */}
        <Grid
          templateColumns={{
            base: '1fr',
            md: 'repeat(4, minmax(0, max-content))',
          }}
          gap={{ base: '16px', md: '24px' }}
        >
          <DetailSection label="Department">
            <KTag
              width="fit-content"
              maxW="100%"
              value={
                <TruncatedTextWithTooltip
                  text={requisition.ats_department.name}
                />
              }
            />
          </DetailSection>
          <DetailSection label="Office">
            <ViewMorePopover
              list={requisition.ats_offices}
              renderInitial={() =>
                requisition.ats_offices.slice(0, 1).map((location, index) =>
                  location ? (
                    <TruncatedTextWithTooltip
                      key={index}
                      maxW="100%"
                      textAlign="start"
                      borderRadius="4px"
                      size="sm-medium"
                      p={'0.125em 0.5em'}
                      bgColor={'secondary-50'}
                      _groupHover={{
                        bg: '#fff',
                        borderColor: 'border-color',
                      }}
                      text={location.name}
                    />
                  ) : (
                    <KText key={index} fontSize="sm" color="secondary-500">
                      -
                    </KText>
                  )
                )
              }
              renderOthers={() => (
                <Flex flexDir="column">
                  {requisition.ats_offices?.map((i, ind) => (
                    <Flex key={ind} alignItems="center" gap=".25em" mb="xs">
                      <Box
                        w="2px"
                        h="2px"
                        bgColor={'secondary-700'}
                        borderRadius="full"
                      ></Box>
                      <KText
                        key={i?.id}
                        size="sm-medium"
                        color="secondary-700"
                        textTransform="capitalize"
                      >
                        {i.name}
                      </KText>
                    </Flex>
                  ))}
                </Flex>
              )}
              popOverStyles={{
                maxW: '15em',
              }}
              moreTextStyles={{
                size: 'sm-medium',
                color: 'secondary-700',
                _hover: { color: 'primary-500' },
                p: '0.125em 0.5em',
                bgColor: 'secondary-50',
                borderRadius: '4px',
                border: '1px solid',
                borderColor: 'transparent',
                _groupHover: { bg: '#fff', borderColor: 'border-color' },
              }}
              type="requisition"
            />
          </DetailSection>
          <DetailSection label="Work type">
            <KTag
              width="fit-content"
              maxW="100%"
              value={
                EMPLOYMENT_TYPE_CONFIG[requisition.employment_type]?.label ||
                requisition.employment_type
              }
            />
          </DetailSection>
          <DetailSection label="Requisition type">
            <KTag
              width="fit-content"
              maxW="100%"
              value={
                REQUISITION_TYPE_CONFIG[requisition.requisition_type]?.label ||
                requisition.requisition_type
              }
            />
          </DetailSection>
        </Grid>

        {/* Salary */}
        {isFieldVisible('salary') && (
          <DetailSection label="Salary">
            <KText wordBreak="break-word">{formatSalary()}</KText>
          </DetailSection>
        )}

        {/* People: Recruitment Partner and Hiring Manager */}
        <Grid
          templateColumns={{
            base: '1fr',
            md: 'repeat(2, minmax(0, max-content))',
          }}
          gap={{ base: '16px', md: '24px' }}
        >
          <DetailSection label="Recruitment partner">
            <UserInfoSection
              user={requisition.recruiter}
              showReassign={
                !readOnlyMode &&
                !!requisition.recruiter &&
                canEdit &&
                !isReqAssociated
              }
              showAssign={
                !readOnlyMode &&
                !requisition.recruiter &&
                canEdit &&
                !isReqAssociated
              }
              onReassign={handleRecruiterAssign}
              onAssign={handleRecruiterAssign}
              reassignTitle="Re-assign requisition"
              reassignDescription="Assign this requisition to a different recruiter"
              reassignFieldLabel="Select recruitment partner"
              assignTitle="Assign recruitment partner"
              assignDescription="Assign a recruitment partner to this requisition"
              assignFieldLabel="Select recruitment partner"
              isLoading={isUpdatingRecruiter}
              isPartOfGroup={isPartOfGroup}
              groupRequisitions={groupRequisitions}
              currentRequisitionId={currentRequisitionId || requisition.id}
            />
          </DetailSection>
          <DetailSection label="Hiring manager">
            <UserInfoSection user={requisition.hiring_manager} />
          </DetailSection>
        </Grid>

        {/* Target Dates */}
        {(isFieldVisible('target_hire_date') ||
          isFieldVisible('target_start_date')) && (
          <Grid
            templateColumns={{
              base: '1fr',
              md: 'repeat(2, minmax(0, max-content))',
            }}
            gap={{ base: '16px', md: '24px' }}
          >
            {isFieldVisible('target_hire_date') && (
              <DetailSection label="Target hire date">
                <KText>{formatDate(requisition.target_hire_date)}</KText>
              </DetailSection>
            )}
            {isFieldVisible('target_start_date') && (
              <DetailSection label="Target start date">
                <KText>{formatDate(requisition.target_start_date)}</KText>
              </DetailSection>
            )}
          </Grid>
        )}

        {/* Custom Fields */}
        <CustomFieldsSection
          additionalInfo={requisition.additional_info || {}}
          customFieldsData={customFieldsData}
          dateFormat={dateFormat}
          departmentId={requisition.ats_department.id}
          officeIds={requisition.ats_offices.map((office) => office.id)}
        />

        {/* Associated Job */}
        {requisition.ats_job && (
          <DetailSection
            label="Associated job"
            tooltipLabel="
              Requisitions associated with jobs will open automatically after approval, or immediately upon creation if no approval process is required
					"
          >
            <Flex alignItems="center" gap="6px">
              {requisition.ats_job.confidential && !requisition.ats_job.name && (
                <Flex flexShrink={0}>
                  <Tooltip
                    label="The job associated with this requisition is confidential"
                    hasArrow
                    placement="top"
                  >
                    <Box display="inline-block">
                      <KTag value="Confidential" />
                    </Box>
                  </Tooltip>
                </Flex>
              )}
              {requisition.ats_job.name && (
                <Link
                  href={`/jobs/${requisition.ats_job.id}`}
                  color="primary-600"
                  fontWeight="medium"
                  fontSize="sm"
                  _hover={{ textDecoration: 'underline' }}
                  display="flex"
                  alignItems="center"
                  flex="1"
                  p="12px"
                  borderRadius="6px"
                  border="1px solid"
                  borderColor="border-color"
                  bg="secondary-25"
                  outline="none"
                  _focus={{}}
                  isExternal
                  gap="4px"
                >
                  {requisition.ats_job.name}
                  <Icon as={LinkExternal01} boxSize="0.75rem" />
                </Link>
              )}
              {requisition.ats_job.name &&
                !readOnlyMode &&
                canEdit &&
                !isReqAssociated && (
                  <AssociateJobPopover
                    onAssociate={(jobId, requisitionId, applyToGroup) => {
                      associateJob({
                        requisitionId: (
                          requisitionId || requisition.id
                        ).toString(),
                        jobId,
                        applyToGroup,
                      });
                    }}
                    currentJobId={requisition.ats_job.id}
                    isLoading={isAssociatingJob}
                    isEditing={true}
                    isPartOfGroup={isPartOfGroup}
                    groupRequisitions={groupRequisitions}
                    currentRequisitionId={
                      currentRequisitionId || requisition.id
                    }
                    triggerElement={
                      <IconButton
                        aria-label="Change associated job"
                        icon={<Icon as={Edit05} boxSize="1rem" />}
                        variant="quaternary"
                        color="secondary-500"
                        isDisabled={isAssociatingJob}
                        paddingInline="8px"
                      />
                    }
                    variant="unstyled"
                  />
                )}
            </Flex>
          </DetailSection>
        )}

        {!requisition.ats_job && !readOnlyMode && canEdit && !isReqAssociated && (
          <Flex direction="column" gap="12px" alignItems="flex-start">
            <AssociateJobPopover
              onAssociate={(jobId, requisitionId, applyToGroup) => {
                associateJob({
                  requisitionId: (requisitionId || requisition.id).toString(),
                  jobId,
                  applyToGroup,
                });
              }}
              isLoading={isAssociatingJob}
              isPartOfGroup={isPartOfGroup}
              groupRequisitions={groupRequisitions}
              currentRequisitionId={currentRequisitionId || requisition.id}
            />

            {jobPermissions.create && (
              <Flex alignItems="center" gap="6px">
                <KText size="sm-regular" color="secondary-600" mb="3px">
                  or
                </KText>
                <KIconButton
                  icon={PlusCircle}
                  onClick={createJobModalActions.onOpen}
                  text="Create new job"
                  iconProps={{
                    width: '12px',
                    height: '12px',
                    color: 'primary-600',
                  }}
                  iconContainerProps={{ width: '12px', height: '12px' }}
                  textProps={{ color: 'primary-600' }}
                  gap="3px"
                />
              </Flex>
            )}
          </Flex>
        )}

        {/* Associated Candidate */}
        {requisition.requisition_association && (
          <AssociatedCandidateSection
            requisitionAssociation={requisition.requisition_association}
            jobId={requisition.ats_job?.id}
            dateFormat={dateFormat}
            filledBy={requisition.filled_by}
            requisitionSalaryRange={{
              minimum_salary: requisition.minimum_salary,
              maximum_salary: requisition.maximum_salary,
              salary_currency: salaryCurrency.currency_code,
            }}
          />
        )}
      </Flex>

      {/* Reason Modal for Reapproval */}
      {isReasonModalOpen && (
        <ModalWithForm
          isOpen={isReasonModalOpen}
          onClose={() => {
            setIsReasonModalOpen(false);
            setPendingRecruiterId(null);
          }}
          headerText="Changes need approval"
          subHeaderText="Your changes will be saved and the requisition will be sent for approval again. Are you sure?"
          primaryCtaText="Save and send for approval"
          handleSubmit={handleReasonSubmit}
          formConfig={getEditReasonFormConfig()}
          initialValues={{ reapproval_note: '' }}
          isCtaLoading={isUpdatingRecruiter}
          skipDirtyCheck={true}
          showAdditionalBlock={true}
          AdditionalBlock={renderApprovalFlow()}
        />
      )}

      {/* Create Job Modal */}
      {createJobModal}
    </Box>
  );
};
